import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import CallFormCard from "@/components/CallFormCard";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Sheet } from "lucide-react";
import { fmtDateTime } from "@/lib/date";
import { AdminSync } from "@/pages/Vetting";
import Topic, { GUIDE } from "@/components/newpartner/Topic";
import GuideSearch from "@/components/newpartner/GuideSearch";

function SectionNav({ data, current }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="lg:hidden">
        <label htmlFor="np-section-select" className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Choose a section</label>
        <Select value={current} onValueChange={(v) => navigate(`${GUIDE}/${v}`)}>
          <SelectTrigger id="np-section-select" className="mt-1 h-11 bg-white" data-testid="np-section-select"><SelectValue /></SelectTrigger>
          <SelectContent>{data.stages.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`np-select-${s.id}`}>{s.nav}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <nav className="hidden lg:block sticky top-24 self-start space-y-0.5" aria-label="New Partner Guide sections" data-testid="np-nav">
        {data.stages.map((s) => (
          <Link key={s.id} to={`${GUIDE}/${s.id}`} data-testid={`np-nav-${s.id}`} aria-current={s.id === current ? "page" : undefined}
                className={`block rounded-xl px-3 py-2 text-sm transition-colors ${s.id === current ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-white"}`}>{s.nav}</Link>
        ))}
      </nav>
    </>
  );
}

function QuickLinks({ links }) {
  return (
    <div className="space-y-1.5" data-testid="np-quick-links">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold">Quick links</div>
      {links.map((q) => (
        <Link key={q.label} to={q.to || `${GUIDE}/${q.section}#${q.topic}`} data-testid={`np-quick-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="flex items-center gap-3 rounded-lg bg-[#0A1F44] text-white px-4 py-3 text-sm font-semibold hover:ring-2 hover:ring-[#C9A961] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961]">
          <span className="flex-1">{q.label}</span><ArrowRight className="w-4 h-4 text-[#C9A961]" />
        </Link>
      ))}
    </div>
  );
}

function useHashTarget(stageId, data) {
  const { hash } = useLocation();
  const id = hash.slice(1) || null;
  useEffect(() => {
    if (!data) return;
    if (!id) { window.scrollTo({ top: 0 }); return; }
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    return () => clearTimeout(t);
  }, [stageId, data, id]);
  return id;
}

export default function NewPartnerGuide() {
  const { stageId } = useParams();
  const { user, isViewAs } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/training/newpartner").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const target = useHashTarget(stageId, data);
  const [open, setOpen] = useState({ sid: null, ids: new Set() });
  const current = data && (data.stages.find((s) => s.id === stageId) || data.stages[0]);
  useEffect(() => {
    if (current) setOpen({ sid: current.id, ids: new Set(current.topics.slice(0, 2).map((t) => t.id)) });
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (target) setOpen((o) => (o.ids.has(target) ? o : { ...o, ids: new Set([...o.ids, target]) }));
  }, [target, open.sid]);
  const toggle = (tid) => setOpen((o) => {
    const ids = new Set(o.ids);
    if (ids.has(tid)) ids.delete(tid); else ids.add(tid);
    return { ...o, ids };
  });
  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading guide…</div>;
  const sec = current;
  const isOpen = (tid) => (open.sid === sec.id ? open.ids.has(tid) : sec.topics.slice(0, 2).some((t) => t.id === tid));
  const [secTitle, tagline, intro] = sec.id === "start-here" ? [sec.header[2], null, null] : sec.header;
  return (
    <div className="space-y-6" data-testid="new-partner-guide-page">
      <section className="rounded-2xl bg-[#0A1F44] text-white p-5 sm:p-7 ring-1 ring-[#C9A961]/30">
        <Link to="/hub" className="inline-flex items-center min-h-[44px] text-sm text-white/80 hover:text-white">← Resources</Link>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1" data-testid="np-title">{data.title}</h1>
        <p className="text-white/70 text-sm mt-1 max-w-2xl" data-testid="np-subtitle">{data.subtitle}</p>
        {user?.role === "admin" && !isViewAs && <a href={data.source_sheet} target="_blank" rel="noopener noreferrer" data-testid="np-open-sheet"
           className="inline-flex items-center gap-1 text-[11px] text-[#C9A961] hover:text-white mt-3"><Sheet className="w-3.5 h-3.5" /> Open Google Sheet (admin)</a>}
      </section>
      <GuideSearch data={data} />
      <div className="grid gap-6 lg:grid-cols-[250px_1fr]">
        <SectionNav data={data} current={sec.id} />
        <article className="space-y-4 min-w-0" data-testid={`np-section-${sec.id}`}>
          <header>
            {sec.nav.toUpperCase() !== (secTitle || "").toUpperCase() && <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">{sec.nav}</div>}
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0A1F44] mt-1" data-testid="np-section-title">{sec.nav.toUpperCase() === (secTitle || "").toUpperCase() ? sec.nav : secTitle}</h2>
            {tagline && <p className="text-sm font-medium text-[#8A6D1F] mt-1">{tagline}</p>}
            {intro && <p className="text-sm text-slate-600 mt-1">{intro}</p>}
          </header>
          {sec.id === "start-here" && <QuickLinks links={data.quick_links} />}
          {sec.id === "start-here" && <CallFormCard testid="np-call-form" />}
          {sec.topics.map((t, ti) => <Topic key={t.id} t={t} sid={sec.id} primary={ti < 2} highlight={target === t.id} open={isOpen(t.id)} onToggle={() => toggle(t.id)} />)}
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-600 pt-4" data-testid="np-synced-at">
            Content last synced from the master sheet {fmtDateTime(data.synced_at)} (Melbourne)
            {user?.can_sync_content && !isViewAs && <AdminSync slug="newpartner" onDone={load} />}
          </div>
        </article>
      </div>
    </div>
  );
}
